'use client';

import { useState, useRef, useEffect } from 'react';

/**
 * A searchable dropdown component that supports filtering and custom values.
 */
export default function SearchableDropdown({
    label,
    name,
    value,
    options = [],
    onChange,
    placeholder = 'Search...',
    required = false,
    strict = false,
    loading = false,
    error = '',
    className = ''
}) {
    const [isOpen, setIsOpen] = useState(false);
    const [activeIndex, setActiveIndex] = useState(-1);
    const containerRef = useRef(null);
    const listRef = useRef(null);

    // Resolve the display label from the current value
    const getLabelFromValue = (val) => {
        if (val == null || val === '') return '';
        const match = options.find(o => (typeof o === 'string' ? o : o.value) == val);
        return match ? (typeof match === 'string' ? match : match.label) : String(val);
    };

    const [searchTerm, setSearchTerm] = useState(() => getLabelFromValue(value));

    useEffect(() => {
        setSearchTerm(getLabelFromValue(value));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value, options]);

    useEffect(() => {
        if (!isOpen) {
            setActiveIndex(-1);
        }
    }, [isOpen]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (containerRef.current && !containerRef.current.contains(event.target)) {
                setIsOpen(false);
                // If strict, reset search term to the label on close
                if (strict) {
                    setSearchTerm(getLabelFromValue(value));
                }
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [value, strict]);

    const filteredOptions = options.filter(option => {
        const label = typeof option === 'string' ? option : option.label;
        if (label == null) return false;
        return label.toLowerCase().includes(String(searchTerm).toLowerCase());
    });

    const handleInputChange = (e) => {
        const val = e.target.value;
        setSearchTerm(val);
        setIsOpen(true);
        setActiveIndex(-1);
        if (!strict) {
            onChange({ target: { name, value: val } });
        }
    };

    const handleSelect = (option) => {
        if (!option) return;
        const optValue = typeof option === 'string' ? option : option.value;
        const optLabel = typeof option === 'string' ? option : option.label;
        setSearchTerm(optLabel);
        setIsOpen(false);
        onChange({ target: { name, value: optValue } });
    };

    const handleKeyDown = (e) => {
        if (!isOpen && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
            setIsOpen(true);
            return;
        }

        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                setActiveIndex(prev => (prev < filteredOptions.length - 1 ? prev + 1 : 0));
                break;
            case 'ArrowUp':
                e.preventDefault();
                setActiveIndex(prev => (prev > 0 ? prev - 1 : filteredOptions.length - 1));
                break;
            case 'Enter':
                if (activeIndex >= 0 && activeIndex < filteredOptions.length) {
                    e.preventDefault();
                    handleSelect(filteredOptions[activeIndex]);
                }
                break;
            case 'Escape':
                setIsOpen(false);
                if (strict) setSearchTerm(getLabelFromValue(value));
                break;
            case 'Tab':
                setIsOpen(false);
                break;
            default:
                break;
        }
    };

    // Auto-scroll highlighted item into view
    useEffect(() => {
        if (activeIndex >= 0 && listRef.current) {
            const activeItem = listRef.current.children[activeIndex];
            if (activeItem) {
                activeItem.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
            }
        }
    }, [activeIndex]);

    return (
        <div className={`mb-4 w-full relative ${className}`} ref={containerRef}>
            <label className="block text-sm font-medium text-gray-700 mb-2">
                {label} {required && <span className="text-red-500">*</span>}
            </label>

            <div className="relative">
                <input
                    type="text"
                    name={name}
                    value={searchTerm}
                    onChange={handleInputChange}
                    onKeyDown={handleKeyDown}
                    onFocus={() => setIsOpen(true)}
                    placeholder={placeholder}
                    className="input w-full pr-10"
                    required={required}
                    autoComplete="off"
                />

                <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                    {loading ? (
                        <svg className="animate-spin h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                    ) : (
                        <svg className={`h-4 w-4 text-gray-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                    )}
                </div>

                {isOpen && (filteredOptions.length > 0) && (
                    <div
                        ref={listRef}
                        className="absolute z-[100] w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-xl max-h-60 overflow-y-auto animate-fadeIn"
                    >
                        {filteredOptions.map((option, index) => {
                            const optLabel = typeof option === 'string' ? option : option.label;
                            const optValue = typeof option === 'string' ? option : option.value;
                            const isActive = index === activeIndex;
                            const isSelectedValue = value === optValue;

                            return (
                                <div
                                    key={index}
                                    className={`px-4 py-2.5 text-sm cursor-pointer transition-colors ${isActive
                                        ? 'bg-primary/10 text-primary font-medium'
                                        : isSelectedValue
                                            ? 'bg-primary/5 text-primary'
                                            : 'text-gray-700 hover:bg-gray-50'
                                        }`}
                                    onMouseEnter={() => setActiveIndex(index)}
                                    onClick={() => handleSelect(option)}
                                >
                                    {optLabel}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
            {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
        </div>
    );
}
